import { useBreakpoint } from '@insify/react-hooks';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { graphql } from 'gatsby';
import { Section } from '~components/Section';
import { Heading } from '~components/Heading';
import { Image } from '~components/Image';
import { Lottie } from '~components/Lottie';
import { Carousel, CarouselPageIndicator, CarouselPagination, CarouselSlider } from '~components/Carousel';
import { replaceWithNonBreakingHyphen } from '~utils/string';
import { flipClamp } from '~utils/number';
import { calcMaxDimensionsDesktop, calcMaxDimensionsMobile } from './utils';

import * as styles from './ComponentUspCarousel.module.css';

import type { ContentBlock } from '~components/types';
import type { ImageType } from '~components/Image';
import type { LottieType } from '~components/Lottie';
import type { ContentfulComponentUspCarousel } from './types';

type ComponentUspCarouselProps = {
  content: ContentfulComponentUspCarousel;
};

export const ComponentUspCarousel: ContentBlock<ComponentUspCarouselProps> = ({
  content,
  className,
  ...sectionProps
}) => {
  const [activeItemIndex, setActiveItemIndex] = useState<number>(0);
  const isDesktop = useBreakpoint('(min-width: 960px)');

  useEffect(() => {
    if (!isDesktop) return;

    const timeout = setTimeout(() => {
      setActiveItemIndex((state) => flipClamp(state + 1, 0, content.items.length - 1));
    }, content.carouselInterval);

    return () => clearTimeout(timeout);
  }, [activeItemIndex, isDesktop]);

  return (
    <Section {...sectionProps} className={clsx(className, styles.section)} blockLayout='boxed'>
      <div className={styles.mobileContent}>
        <Carousel
          config={{
            all: {
              slidesToShow: 1,
              slideGap: '0px',
              transitionDurationAutoplay: 1500,
              transitionDurationManual: 300,
              autoplayInterval: content.carouselInterval,
              pauseAutoplayOnHover: true,
            },
          }}
        >
          <CarouselSlider className={styles.carouselSlider}>
            {content.items.map(({ media, heading, description }) => {
              let mediaComponent: JSX.Element | null = null;

              if (media.file.contentType.startsWith('image')) {
                const image = media as ImageType;
                const { width, height } = image.file.details.image;
                const { maxWidth, maxHeight } = calcMaxDimensionsMobile(width, height);
                const aspectRatio = width + ' / ' + height;

                mediaComponent = (
                  <Image
                    image={image}
                    className={clsx(styles.media, styles.image)}
                    style={{
                      aspectRatio,
                      maxWidth,
                      maxHeight,
                    }}
                  />
                );
              } else if (media.file.contentType.startsWith('application')) {
                mediaComponent = (
                  <Lottie
                    src={media.file.url}
                    lottie={media as LottieType}
                    className={clsx(styles.media, styles.lottie)}
                  />
                );
              }

              return (
                <div key={heading} className={styles.carouselSlide}>
                  {mediaComponent}
                  <Heading className={styles.heading}>{replaceWithNonBreakingHyphen(heading)}</Heading>
                  <p className={styles.description}>{replaceWithNonBreakingHyphen(description)}</p>
                </div>
              );
            })}
          </CarouselSlider>
          <CarouselPagination
            className={styles.carouselPagination}
            PageIndicatorComponent={({ index }) => (
              <CarouselPageIndicator
                className={styles.carouselPageIndicator}
                activateOnIndex={index}
                progressBarMode
              ></CarouselPageIndicator>
            )}
          />
        </Carousel>
      </div>
      <div className={styles.desktopContent}>
        <div className={styles.mediaContainer}>
          {content.items.map(({ media, heading, description }, index) => {
            if (media.file.contentType.startsWith('image')) {
              const image = media as ImageType;
              const { width, height } = image.file.details.image;
              const { maxWidth, maxHeight } = calcMaxDimensionsDesktop(width, height);
              const aspectRatio = width + ' / ' + height;

              return (
                <div key={`${heading}-${description}`} className={styles.mediaWrapper}>
                  <Image
                    image={image}
                    style={{
                      aspectRatio,
                      maxWidth,
                      maxHeight,
                    }}
                    className={clsx(styles.media, styles.image, index === activeItemIndex && styles.active)}
                  />
                </div>
              );
            }
            return (
              <div key={`${heading}-${description}`} className={styles.mediaWrapper}>
                <Lottie
                  className={clsx(styles.media, styles.lottie, index === activeItemIndex && styles.active)}
                  src={media.file.url}
                  lottie={media as LottieType}
                />
              </div>
            );
          })}
        </div>
        <div className={styles.itemContainer}>
          {content.items.map(({ heading, description }, index) => (
            <div
              key={`${heading}-${description}`}
              className={clsx(styles.item, index === activeItemIndex && styles.active)}
              onClick={() => {
                setActiveItemIndex(index);
              }}
            >
              <div className={styles.progressBar} style={{ animationDuration: `${content.carouselInterval}ms` }} />
              <Heading className={styles.heading} level={1}>
                {replaceWithNonBreakingHyphen(heading)}
              </Heading>
              <p className={styles.description}>{replaceWithNonBreakingHyphen(description)}</p>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};

export const query = graphql`
  fragment ComponentUspCarousel on ContentfulComponentUspCarousel {
    __typename
    items {
      media {
        ...MixedMedia
      }
      heading
      description
    }
    carouselInterval
  }
`;
